import { useTranslation } from 'react-i18next';
import React, {useState, useEffect} from 'react';
import httpService from "../../../services/http.service";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";
import {Link, useHistory} from "react-router-dom";
import {truncate} from "../../../utils";
import AppPagination from "../../Shared/AppPagination";
import KpiCard from "../../Shared/KpiCard";
const queryString = require('query-string');

const FILTER_KEYS = ['keyword', 'ago_id', 'activity', 'sort'];
const formatInt = (value) => (value === undefined || value === null ? '–' : Number(value).toLocaleString());

/** Carte d'une agora : toute la carte ouvre ses opinions, sauf les boutons. */
const AgoraCard = ({agora, social}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const opinionsUrl = `/agoras/${agora.ago_id}/opinions`;
    const onClick = (e) => {
        if (e.target.closest('a, button')) return;
        if (window.getSelection && String(window.getSelection()).length > 0) return;
        history.push(opinionsUrl);
    };

    return (
        <div className="card agora-card" role="link" tabIndex={0} onClick={onClick}
             onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && history.push(opinionsUrl)}>
            <div className="agora-card__main">
                <img loading="lazy" className="agora-card__picture" src={agora.ago_picture || MyOpinionLogo} alt="" />
                <div className="agora-card__identity">
                    <div className="agora-card__title" title={agora.ago_title}>{agora.ago_title}</div>
                    <div className="agora-card__date"><i className="fa fa-calendar"></i> {t("Créée le {{d}}", {d: agora.ago_created})}</div>
                </div>
            </div>
            <p className="agora-card__presentation">
                {agora.ago_presentation ? truncate(agora.ago_presentation, 140) : <em className="text-muted">{t("Aucune description")}</em>}
            </p>
            <div className="agora-card__footer">
                <span><i className="fa fa-users"></i> <strong>{formatInt(agora.users_count)}</strong> {t("membre(s)")}</span>
                <span><i className="fa fa-pencil"></i> <strong>{formatInt(agora.opinions_count)}</strong> {social ? t("post(s)") : t("opinion(s)")}</span>
                <span className="agora-card__actions">
                    <Link to={`/agoras/${agora.ago_id}/members`} className="btn btn-sm btn-outline-success"><i className="fa fa-users"></i>{' '}{t("Membres")}</Link>
                    <Link to={opinionsUrl} className="btn btn-sm btn-outline-info"><i className="fa fa-edit"></i>{' '}{social ? t("Posts") : t("Opinions")}</Link>
                </span>
            </div>
        </div>
    );
};

/** Agoras (/agoras) : carte de statistiques, recherche, filtre d'activité et tri dans l'URL, cartes cliquables. */
const AgoraList = ({history, location}) => {
    const { t } = useTranslation();
    const query = queryString.parse(location.search);
    const social = !!process.env.REACT_APP_SOCIAL_HUB;

    const [agoras, setAgoras] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [meta, setMeta] = useState(null);
    const [stats, setStats] = useState(null);
    const [keyword, setKeyword] = useState(query.keyword || '');

    const setFilters = (changes) => {
        const next = {...query, ...changes};
        if (!('page' in changes)) delete next.page;
        Object.keys(next).forEach(key => (next[key] === '' || next[key] === undefined || next[key] === null) && delete next[key]);
        history.push(`/agoras?${queryString.stringify(next)}`);
    };

    useEffect(() => {
        (async () => {
            try {
                const {data} = await httpService.get('/agoras/stats');
                setStats(data?.data || null);
            } catch (e) {
                setStats(null);
            }
        })();
    }, []);

    useEffect(() => {
        setKeyword(query.keyword || '');
        setFetching(true);
        (async () => {
            try {
                const params = {page: query.page || 1};
                FILTER_KEYS.forEach(key => query[key] && (params[key] = query[key]));
                const {data: response} = await httpService.get('/agoras', {params});
                setAgoras(response.data || []);
                setMeta({
                    currentPage: response?.meta?.current_page,
                    lastPage: response?.meta?.last_page,
                    totalItems: response?.meta?.total,
                    hasNext: !!response?.links?.next,
                    hasPrevious: !!response?.links?.prev,
                });
            } catch (e) {
                setAgoras([]);
                setMeta(null);
            } finally {
                setFetching(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const onPaginate = (direction) => {
        const current = parseInt(query.page || 1, 10);
        setFilters({page: direction === 'previous' ? current - 1 : current + 1});
    };

    const share = (value) => (stats && stats.total ? Math.round((value / stats.total) * 100) : 0);
    const hasFilters = ['keyword', 'ago_id', 'activity'].some(key => query[key]);

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={social ? t("Groupes") : t("Agoras")} />

            {/* Statistiques */}
            <div className="row clearfix equal-height-row">
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-globe" tone="blue" label={social ? t("Groupes") : t("Agoras")} loading={!stats}
                             value={formatInt(stats?.total)} hint={t("+{{n}} créée(s) en 30 jours", {n: formatInt(stats?.created_30_days)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-bolt" tone="green" label={t("Actives (30 jours)")} loading={!stats}
                             value={formatInt(stats?.active_30_days)} progress={share(stats?.active_30_days)}
                             hint={t("{{n}} publication(s) en 30 jours", {n: formatInt(stats?.opinions_30_days)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-users" tone="orange" label={t("Adhésions")} loading={!stats}
                             value={formatInt(stats?.members)} hint={t("{{n}} membre(s) distinct(s)", {n: formatInt(stats?.distinct_members)})} />
                </div>
                <div className="col-lg-3 col-md-6">
                    <KpiCard icon="fa-user-times" tone="purple" label={t("Sans membre")} loading={!stats}
                             value={formatInt(stats?.empty)} hint={t("{{p}} % du total", {p: share(stats?.empty)})} />
                </div>
            </div>

            {/* Recherche et filtres */}
            <div className="card">
                <div className="body">
                    <form className="row" onSubmit={(e) => { e.preventDefault(); setFilters({keyword: keyword.trim(), ago_id: ''}); }}>
                        <div className="col-lg-6 col-md-12 mb-2">
                            <div className="input-group">
                                <input className="form-control" placeholder={t("Titre ou présentation")} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                                <div className="input-group-append"><button className="btn btn-primary" type="submit"><i className="fa fa-search"></i></button></div>
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.activity || ''} onChange={(e) => setFilters({activity: e.target.value})}>
                                <option value="">{t("Toutes les agoras")}</option>
                                <option value="active">{t("Actives (30 jours)")}</option>
                                <option value="inactive">{t("Sans activité (30 jours)")}</option>
                                <option value="empty">{t("Sans membre")}</option>
                            </select>
                        </div>
                        <div className="col-lg-3 col-md-6 mb-2">
                            <select className="form-control" value={query.sort || ''} onChange={(e) => setFilters({sort: e.target.value})}>
                                <option value="">{t("Plus récentes")}</option>
                                <option value="members">{t("Plus de membres")}</option>
                                <option value="opinions">{t("Plus de publications")}</option>
                                <option value="oldest">{t("Plus anciennes")}</option>
                            </select>
                        </div>
                    </form>
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{meta ? t("{{n}} résultat(s)", {n: formatInt(meta.totalItems)}) : ''}</small>
                        {hasFilters && (
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => history.push('/agoras')}>
                                <i className="fa fa-times"></i>{' '}{t("Effacer les filtres")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {fetching ? <FecthingSpinner /> : (
                <div className="row clearfix equal-height-row">
                    {agoras.length === 0 ? (
                        <div className="col-md-12">
                            <div className="card"><div className="body text-center text-muted py-5">{t("Aucune agora ne correspond aux filtres.")}</div></div>
                        </div>
                    ) : agoras.map(agora => (
                        <div className="col-xl-4 col-lg-6 col-md-12" key={agora.ago_id}>
                            <AgoraCard agora={agora} social={social} />
                        </div>
                    ))}
                    {meta && meta.lastPage > 1 && (
                        <div className="col-md-12">
                            <div className="card">
                                <div className="body">
                                    <AppPagination
                                        onNavigate={onPaginate}
                                        currentPage={meta.currentPage}
                                        hasNext={meta.hasNext}
                                        hasPrevious={meta.hasPrevious}
                                        totalItems={meta.totalItems}
                                        lastPage={meta.lastPage}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AgoraList;
